import { AlertTriangle } from 'lucide-react';
import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';

type ForecastPaginatedActionsWarningLastProductModalProps = {
  open: boolean;
  confirmLabel: string;
  nextSessionName: string;
  requireNextSessionName: boolean;
  onNextSessionNameChange: (value: string) => void;
  handleConfirm: () => void;
  handleCancel: () => void;
};

export function ForecastPaginatedActionsWarningLastProductModal({
  open,
  confirmLabel,
  nextSessionName,
  requireNextSessionName,
  onNextSessionNameChange,
  handleConfirm,
  handleCancel,
}: ForecastPaginatedActionsWarningLastProductModalProps) {
  const { t } = useTranslation('forecast');
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNextSessionNameChange(event.target.value);
  };

  return (
    <Dialog open={open}>
      <DialogContent className='max-h-[80vh] max-w-xl' closable={false}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-warning-default' />
            {t('Are you sure')}
          </DialogTitle>
          <DialogDescription className='flex flex-col gap-1 pt-2'>
            <span>{t('You are about to process the last product')}</span>
            <span>
              {t(
                'It will close this session and create a new empty one only if no other open sessions are found',
              )}
            </span>
            {requireNextSessionName ? (
              <span>
                {t(
                  'If a new empty session is needed, it will use the name below',
                )}
              </span>
            ) : null}
            <span>{t('This action cannot be undone')}</span>
          </DialogDescription>
        </DialogHeader>
        {requireNextSessionName ? (
          <div className='grid gap-2'>
            <span className='text-sm font-medium text-layout-foreground'>
              {t('Next session name')}
            </span>
            <Input
              value={nextSessionName}
              onChange={handleInputChange}
              placeholder={t('Next session name')}
              maxLength={255}
              autoFocus
            />
          </div>
        ) : null}
        <div className='flex w-full flex-row justify-end gap-4'>
          <Button variant='outline' onClick={handleCancel}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={requireNextSessionName && !nextSessionName.trim()}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
